import {
  getClientHistory,
  createClientInteraction,
  deleteClientInteraction,
} from '@services/requests/housien-services/ClientInsightsService';
import type {
  HistoryEvent,
  PaginatedHistory,
  ClientInteraction,
} from '@services/requests/housien-services/ClientInsightsService';

/**
 * Attempts to create a client interaction, swallowing failures.
 *
 * @param agencyId - The agency identifier.
 * @param clientId - The client identifier.
 * @param body - The partial interaction payload.
 * @returns True when the interaction was created, false on error.
 */
export async function tryCreateInteraction(
  agencyId: string, clientId: string, body: Partial<ClientInteraction>,
): Promise<boolean> {
  try {
    await createClientInteraction(agencyId, clientId, body);
    return true;
  } catch {
    return false;
  }
}

/**
 * Attempts to delete a client interaction, swallowing failures.
 *
 * @param agencyId - The agency identifier.
 * @param clientId - The client identifier.
 * @param interactionId - The interaction identifier to delete.
 * @returns True when the interaction was deleted, false on error.
 */
export async function tryDeleteInteraction(
  agencyId: string, clientId: string, interactionId: string,
): Promise<boolean> {
  try {
    await deleteClientInteraction(agencyId, clientId, interactionId);
    return true;
  } catch {
    return false;
  }
}

export interface FetchPageSetters {
  setEvents: (updater: (prev: HistoryEvent[]) => HistoryEvent[]) => void;
  setPage: (n: number) => void;
  setTotalPages: (n: number) => void;
  setTotalItems: (n: number) => void;
  setIsLoading: (v: boolean) => void;
  setIsError: (v: boolean) => void;
}

/**
 * Applies a successful history fetch to component state.
 *
 * @param result - The paginated history result.
 * @param targetPage - The page number that was fetched.
 * @param append - Whether to append to (true) or replace (false) existing events.
 * @param s - The state setters to update.
 */
function applyFetchSuccess(
  result: PaginatedHistory, targetPage: number, append: boolean, s: FetchPageSetters,
): void {
  s.setEvents((prev) => (append ? [...prev, ...result.data] : result.data));
  s.setPage(targetPage);
  s.setTotalPages(result.pages);
  s.setTotalItems(result.items);
  s.setIsLoading(false);
}

/**
 * Applies a failed history fetch to component state, ignoring cancellations.
 *
 * @param err - The caught error.
 * @param s - The state setters to update.
 */
function applyFetchError(err: unknown, s: FetchPageSetters): void {
  if (err instanceof Error && err.name === 'CancelError') return;
  s.setIsError(true);
  s.setIsLoading(false);
}

export interface FetchPageArgs {
  agencyId: string;
  clientId: string;
  pageSize: number;
  targetPage: number;
  append: boolean;
  setters: FetchPageSetters;
  cancelRef: { current: { cancel: () => void } | null };
}

/**
 * Fetches one page of client history and applies the result to state. Cancels
 * any in-flight request first. No-op when agency/client is missing.
 */
export function runFetchPage(args: FetchPageArgs): void {
  const { agencyId, clientId, pageSize, targetPage, append, setters, cancelRef } = args;
  if (!agencyId || !clientId) return;
  setters.setIsLoading(true);
  setters.setIsError(false);
  cancelRef.current?.cancel();
  const promise = getClientHistory(agencyId, clientId, targetPage, pageSize);
  cancelRef.current = promise;
  promise
    .then((result: PaginatedHistory) => applyFetchSuccess(result, targetPage, append, setters))
    .catch((err: unknown) => applyFetchError(err, setters));
}
